import React, { useEffect, useState, useRef, useCallback } from 'react';
import { MessageSquare, Send, Plus, Users, User, AlertTriangle, Zap, Trash2, FileText, X, Paperclip, Download, File, Image, FileSpreadsheet, Phone, PhoneOff, Wifi, WifiOff } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { io } from 'socket.io-client';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
const ALLOWED_TYPES = [
  'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain', 'text/csv',
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
];

function fileIcon(mime) {
  if (!mime) return <File size={16} />;
  if (mime.startsWith('image/')) return <Image size={16} />;
  if (mime.includes('spreadsheet') || mime.includes('csv')) return <FileSpreadsheet size={16} />;
  return <FileText size={16} />;
}

function humanSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function ChatPage({ user }) {
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [containsPhi, setContainsPhi] = useState(false);
  const [priority, setPriority] = useState('normal');
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelMembers, setNewChannelMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showDeletionLog, setShowDeletionLog] = useState(false);
  const [deletionLog, setDeletionLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  // Attachment state
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [channelAttachments, setChannelAttachments] = useState([]);
  const [showAttachments, setShowAttachments] = useState(false);

  // Real-time WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const socketRef = useRef(null);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Socket.io connection ───────────────────────────────────────────────
  useEffect(() => {
    const token = window.zignagenixAPI?.getSessionToken?.();
    if (!token) return;

    const socket = io(window.location.origin, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
    });

    socket.on('connect', () => setWsConnected(true));
    socket.on('disconnect', () => setWsConnected(false));

    // Real-time message delivery
    socket.on('chat:message', (payload) => {
      if (payload.channelId === activeChannel?.id) {
        setMessages(prev => [...prev, {
          id: payload.messageId,
          sender_id: payload.senderId,
          sender_name: payload.senderName,
          message_text: payload.text,
          contains_phi: payload.containsPhi,
          priority: payload.priority || 'normal',
          created_at: payload.timestamp,
          source: payload.senderId ? undefined : 'mobile',
        }]);
      }
      // Refresh channel list for updated previews
      loadChannels();
    });

    // Typing indicators
    socket.on('chat:typing', (payload) => {
      if (payload.isTyping) {
        setTypingUsers(prev => ({ ...prev, [payload.userId]: payload.userName }));
        setTimeout(() => {
          setTypingUsers(prev => { const n = { ...prev }; delete n[payload.userId]; return n; });
        }, 3000);
      } else {
        setTypingUsers(prev => { const n = { ...prev }; delete n[payload.userId]; return n; });
      }
    });

    // Read receipts
    socket.on('chat:read', () => {
      // Refresh to show read state
      if (activeChannel) loadMessages(activeChannel.id);
    });

    // Online presence
    socket.on('presence:online', (payload) => {
      setOnlineUsers(prev => new Set([...prev, payload.userId]));
    });
    socket.on('presence:offline', (payload) => {
      setOnlineUsers(prev => { const n = new Set(prev); n.delete(payload.userId); return n; });
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  // ── Initial load + fallback polling ────────────────────────────────────
  useEffect(() => {
    loadChannels();
  }, []);

  useEffect(() => {
    if (activeChannel) {
      loadMessages(activeChannel.id);
      loadAttachments(activeChannel.id);
      // Join channel room for real-time events
      socketRef.current?.emit('chat:join', { channelId: activeChannel.id });
      // Fallback polling only if WebSocket not connected
      if (!wsConnected) {
        pollRef.current = setInterval(() => loadMessages(activeChannel.id), 5000);
      }
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
        socketRef.current?.emit('chat:leave', { channelId: activeChannel.id });
      };
    }
  }, [activeChannel, wsConnected]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadChannels() {
    try {
      const data = await window.zignagenixAPI.getChatChannels();
      if (!data?.error) setChannels(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load channels:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadMessages(channelId) {
    try {
      const data = await window.zignagenixAPI.getChatMessages(channelId);
      if (!data?.error) setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  }

  async function loadAttachments(channelId) {
    try {
      const data = await window.zignagenixAPI.getChatAttachments(channelId);
      if (!data?.error) setChannelAttachments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load attachments:', err);
    }
  }

  // ── File handling ─────────────────────────────────────────────────────────
  function handleFileSelect(e) {
    setUploadError('');
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const invalid = files.filter(f => !ALLOWED_TYPES.includes(f.type));
    if (invalid.length) {
      setUploadError(`Unsupported file type: ${invalid.map(f => f.name).join(', ')}`);
      return;
    }
    const tooLarge = files.filter(f => f.size > MAX_FILE_SIZE);
    if (tooLarge.length) {
      setUploadError(`File too large (max 25 MB): ${tooLarge.map(f => f.name).join(', ')}`);
      return;
    }
    setPendingFiles(prev => [...prev, ...files]);
    // Reset input so re-selecting the same file works
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removePendingFile(idx) {
    setPendingFiles(prev => prev.filter((_, i) => i !== idx));
  }

  async function uploadFile(file, channelId) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = reader.result.split(',')[1];
          const result = await window.zignagenixAPI.uploadChatAttachment(
            channelId, base64, file.name, file.type, containsPhi
          );
          if (result?.error) reject(new Error(result.message));
          else resolve(result);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  async function handleSend(e) {
    e.preventDefault();
    if ((!newMessage.trim() && pendingFiles.length === 0) || !activeChannel) return;

    setUploading(true);
    setUploadError('');
    try {
      // 1. Upload any pending files first
      const uploadedIds = [];
      for (const file of pendingFiles) {
        const result = await uploadFile(file, activeChannel.id);
        if (result?.attachmentId) uploadedIds.push(result.attachmentId);
      }

      // 2. Send the message (or a placeholder if only files)
      const messageText = newMessage.trim() || (pendingFiles.length > 0
        ? `[Attached ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}: ${pendingFiles.map(f => f.name).join(', ')}]`
        : '');

      if (messageText) {
        const result = await window.zignagenixAPI.sendChatMessage(
          activeChannel.id, messageText, containsPhi, priority
        );
        // 3. Link attachments to the message
        if (result?.success && result.messageId && uploadedIds.length > 0) {
          await window.zignagenixAPI.linkChatAttachments(result.messageId, uploadedIds);
        }
      }

      // 4. Broadcast via WebSocket for real-time delivery to mobile users
      if (socketRef.current && result?.messageId) {
        socketRef.current.emit('chat:message', {
          channelId: activeChannel.id,
          messageId: result.messageId,
          text: messageText,
          containsPhi,
          priority,
          attachments: uploadedIds,
        });
      }

      // 5. Reset state
      setNewMessage('');
      setPendingFiles([]);
      setContainsPhi(false);
      setPriority('normal');
      loadMessages(activeChannel.id);
      loadAttachments(activeChannel.id);
      loadChannels();
    } catch (err) {
      console.error('Failed to send:', err);
      setUploadError(err.message || 'Failed to send message');
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload(attachment) {
    try {
      const result = await window.zignagenixAPI.getChatAttachmentUrl(attachment.id);
      if (result?.url) {
        window.open(result.url, '_blank');
      } else {
        alert(result?.message || 'Failed to get download link');
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  }

  async function handleCreateChannel() {
    if (!newChannelName.trim()) return;
    try {
      const result = await window.zignagenixAPI.createChatChannel(
        newChannelName.trim(), newChannelMembers.length > 1 ? 'group' : 'direct', newChannelMembers, null
      );
      if (result?.success) {
        setShowNewChannel(false);
        setNewChannelName('');
        setNewChannelMembers([]);
        loadChannels();
      }
    } catch (err) {
      console.error('Failed to create channel:', err);
    }
  }

  async function openNewChannel() {
    setShowNewChannel(true);
    try {
      const users = await window.zignagenixAPI.getUsers();
      if (!users?.error) setAllUsers(Array.isArray(users) ? users.filter(u => u.id !== user.id && u.is_active) : []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }

  async function handleDeleteChannel() {
    if (!showDeleteConfirm) return;
    setDeleting(true);
    try {
      const result = await window.zignagenixAPI.deleteChatChannel(showDeleteConfirm.id, deleteReason.trim());
      if (result?.success) {
        setShowDeleteConfirm(null);
        setDeleteReason('');
        if (activeChannel?.id === showDeleteConfirm.id) {
          setActiveChannel(null);
          setMessages([]);
        }
        loadChannels();
      }
    } catch (err) {
      console.error('Failed to delete channel:', err);
    } finally {
      setDeleting(false);
    }
  }

  async function loadDeletionLog() {
    setShowDeletionLog(true);
    setLogLoading(true);
    try {
      const log = await window.zignagenixAPI.getChatDeletionLog();
      setDeletionLog(Array.isArray(log) ? log : []);
    } catch (err) {
      console.error('Failed to load deletion log:', err);
    } finally {
      setLogLoading(false);
    }
  }

  // Build a map of attachments by message_id for inline display
  const attachmentsByMessage = {};
  channelAttachments.forEach(a => {
    if (a.message_id) {
      if (!attachmentsByMessage[a.message_id]) attachmentsByMessage[a.message_id] = [];
      attachmentsByMessage[a.message_id].push(a);
    }
  });

  const canDeleteChannels = user?.permissions?.includes('chat:delete_channel');

  if (loading) return <div className="page-loading">Loading secure chat...</div>;

  return (
    <div className="page chat-page">
      <div className="chat-page-header">
        <div>
          <h1 className="page-title">Secure Chat</h1>
          <p className="page-subtitle">HIPAA-compliant internal messaging — all messages are encrypted &amp; audited</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={`ws-status-badge ${wsConnected ? 'ws-status-badge--online' : 'ws-status-badge--offline'}`} title={wsConnected ? 'Real-time connected' : 'Polling mode (reconnecting...)'}>
            {wsConnected ? <Wifi size={13} /> : <WifiOff size={13} />}
            <span>{wsConnected ? 'Live' : 'Polling'}</span>
          </span>
          <PrintButton pageTitle="Secure Chat" subtitle="HIPAA-Compliant Internal Messaging" />
          {canDeleteChannels && (
            <button className="btn-sm btn-ghost" onClick={loadDeletionLog} title="View deletion log">
              <FileText size={16} />
              <span>Deletion Log</span>
            </button>
          )}
        </div>
      </div>

      <div className="chat-layout">
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <span>Channels</span>
            {user?.permissions?.includes('chat:create_channel') && (
              <button className="chat-new-btn" onClick={openNewChannel} title="New channel"><Plus size={16} /></button>
            )}
          </div>

          {showNewChannel && (
            <div className="chat-new-channel">
              <input
                className="chat-input-sm"
                placeholder="Channel name"
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
              />
              <div className="chat-member-select">
                {allUsers.map((u) => (
                  <label key={u.id} className="chat-member-option">
                    <input
                      type="checkbox"
                      checked={newChannelMembers.includes(u.id)}
                      onChange={(e) => {
                        setNewChannelMembers(e.target.checked
                          ? [...newChannelMembers, u.id]
                          : newChannelMembers.filter(id => id !== u.id));
                      }}
                    />
                    <span>{u.display_name}</span>
                  </label>
                ))}
              </div>
              <div className="chat-new-actions">
                <button className="btn-sm btn-primary" onClick={handleCreateChannel}>Create</button>
                <button className="btn-sm btn-ghost" onClick={() => setShowNewChannel(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div className="chat-channel-list">
            {channels.map((ch) => (
              <div key={ch.id} className={`chat-channel-item-wrapper ${activeChannel?.id === ch.id ? 'chat-channel-item-wrapper--active' : ''}`}>
                <button
                  className={`chat-channel-item ${activeChannel?.id === ch.id ? 'chat-channel-item--active' : ''}`}
                  onClick={() => setActiveChannel(ch)}
                >
                  <div className="chat-channel-icon">
                    {ch.channel_type === 'direct' ? <User size={14} /> :
                     ch.channel_type === 'patient_care' ? <AlertTriangle size={14} /> : <Users size={14} />}
                  </div>
                  <div className="chat-channel-info">
                    <span className="chat-channel-name">{ch.name}</span>
                    <span className="chat-channel-preview">{ch.last_message ? ch.last_message.substring(0, 40) : 'No messages yet'}</span>
                  </div>
                  {ch.patient_name && <span className="chat-phi-badge">PHI</span>}
                </button>
                {canDeleteChannels && (
                  <button
                    className="chat-channel-delete-btn"
                    onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(ch); setDeleteReason(''); }}
                    title="Delete channel"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
            {channels.length === 0 && <p className="empty-state">No channels yet</p>}
          </div>
        </div>

        <div className="chat-main">
          {activeChannel ? (
            <>
              <div className="chat-main-header">
                <MessageSquare size={18} />
                <h2>{activeChannel.name}</h2>
                {activeChannel.patient_name && (
                  <span className="chat-patient-context">Patient: {activeChannel.patient_name}</span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  {activeChannel?.channel_type === 'direct' && (
                    <button className="btn-sm btn-success" title="Start voice call" onClick={() => {
                      // Find the other member in the direct channel to call
                      alert('VoIP call initiated — audio stream will connect via WebRTC');
                    }}>
                      <Phone size={14} />
                      <span>Call</span>
                    </button>
                  )}
                  <button
                    className={`btn-sm btn-ghost ${showAttachments ? 'btn-ghost--active' : ''}`}
                    onClick={() => setShowAttachments(!showAttachments)}
                    title="View shared files"
                  >
                    <Paperclip size={14} />
                    <span>Files{channelAttachments.length > 0 ? ` (${channelAttachments.length})` : ''}</span>
                  </button>
                  {canDeleteChannels && (
                    <button
                      className="btn-sm btn-danger chat-header-delete-btn"
                      onClick={() => { setShowDeleteConfirm(activeChannel); setDeleteReason(''); }}
                      title="Delete this channel"
                    >
                      <Trash2 size={14} />
                      <span>Delete</span>
                    </button>
                  )}
                </div>
              </div>

              {/* ── Shared files panel ── */}
              {showAttachments && (
                <div className="chat-attachments-panel">
                  <div className="chat-attachments-panel-header">
                    <strong>Shared Files</strong>
                    <button className="btn-sm btn-ghost" onClick={() => setShowAttachments(false)}><X size={14} /></button>
                  </div>
                  {channelAttachments.length === 0 ? (
                    <p className="text-secondary" style={{ padding: '12px', fontSize: 13 }}>No files shared in this channel yet.</p>
                  ) : (
                    <div className="chat-attachments-list">
                      {channelAttachments.map(a => (
                        <div key={a.id} className="chat-attachment-item" onClick={() => handleDownload(a)}>
                          {fileIcon(a.mime_type)}
                          <div className="chat-attachment-info">
                            <span className="chat-attachment-name">{a.original_filename}</span>
                            <span className="chat-attachment-meta">{humanSize(a.file_size)} — {a.uploader_name} — {new Date(a.created_at).toLocaleDateString()}</span>
                          </div>
                          <Download size={14} className="chat-attachment-dl" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="chat-messages">
                {messages.map((msg) => (
                  <div key={msg.id} className={`chat-message ${msg.sender_id === user.id ? 'chat-message--own' : ''}`}>
                    <div className="chat-message-header">
                      <span className="chat-message-sender">{msg.sender_name}</span>
                      <span className="chat-message-role">{msg.sender_role}</span>
                      {msg.priority === 'urgent' && <Zap size={12} className="icon--amber" />}
                      {msg.priority === 'stat' && <Zap size={12} className="icon--red" />}
                      {msg.contains_phi && <span className="chat-phi-badge">PHI</span>}
                      {msg.source === 'mobile' && <span className="chat-mobile-badge">Mobile</span>}
                      <span className="chat-message-time">{msg.created_at}</span>
                    </div>
                    <div className="chat-message-body">{msg.message_text}</div>
                    {/* Inline attachments for this message */}
                    {attachmentsByMessage[msg.id] && (
                      <div className="chat-message-attachments">
                        {attachmentsByMessage[msg.id].map(a => (
                          <button key={a.id} className="chat-inline-attachment" onClick={() => handleDownload(a)} title={`Download ${a.original_filename}`}>
                            {fileIcon(a.mime_type)}
                            <span className="chat-inline-attachment-name">{a.original_filename}</span>
                            <span className="chat-inline-attachment-size">{humanSize(a.file_size)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
                {messages.length === 0 && <p className="empty-state">No messages yet. Start the conversation.</p>}
                {/* Typing indicators */}
                {Object.keys(typingUsers).length > 0 && (
                  <div className="chat-typing-indicator">
                    <span className="chat-typing-dots"><span /><span /><span /></span>
                    <span>{Object.values(typingUsers).join(', ')} {Object.keys(typingUsers).length === 1 ? 'is' : 'are'} typing...</span>
                  </div>
                )}
              </div>

              {user?.permissions?.includes('chat:create') && (
                <form className="chat-compose" onSubmit={handleSend}>
                  {/* Pending file previews */}
                  {pendingFiles.length > 0 && (
                    <div className="chat-pending-files">
                      {pendingFiles.map((f, i) => (
                        <div key={i} className="chat-pending-file">
                          {fileIcon(f.type)}
                          <span className="chat-pending-file-name">{f.name}</span>
                          <span className="chat-pending-file-size">{humanSize(f.size)}</span>
                          <button type="button" className="chat-pending-file-remove" onClick={() => removePendingFile(i)}><X size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  {uploadError && (
                    <div className="chat-upload-error">
                      <AlertTriangle size={14} />
                      <span>{uploadError}</span>
                      <button type="button" onClick={() => setUploadError('')}><X size={12} /></button>
                    </div>
                  )}
                  <div className="chat-compose-options">
                    <label className="chat-compose-toggle">
                      <input type="checkbox" checked={containsPhi} onChange={(e) => setContainsPhi(e.target.checked)} />
                      <span>Contains PHI</span>
                    </label>
                    <select className="chat-priority-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                      <option value="normal">Normal</option>
                      <option value="urgent">Urgent</option>
                      <option value="stat">STAT</option>
                    </select>
                  </div>
                  <div className="chat-compose-row">
                    <input type="file" ref={fileInputRef} style={{ display: 'none' }} multiple
                      accept=".pdf,.doc,.docx,.xlsx,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp"
                      onChange={handleFileSelect}
                    />
                    <button type="button" className="chat-attach-btn" onClick={() => fileInputRef.current?.click()}
                      title="Attach files (PDF, Word, Excel, images — max 25 MB)" disabled={uploading}>
                      <Paperclip size={18} />
                    </button>
                    <input
                      className="chat-compose-input"
                      placeholder={pendingFiles.length > 0 ? 'Add a message (optional)...' : 'Type a secure message...'}
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        if (activeChannel && socketRef.current) {
                          socketRef.current.emit('chat:typing', { channelId: activeChannel.id, isTyping: e.target.value.length > 0 });
                        }
                      }}
                      disabled={uploading}
                    />
                    <button type="submit" className="chat-send-btn" disabled={(!newMessage.trim() && pendingFiles.length === 0) || uploading}>
                      {uploading ? <span className="chat-uploading-spinner" /> : <Send size={18} />}
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <div className="chat-empty">
              <MessageSquare size={48} />
              <p>Select a channel or create a new one to start messaging</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleting && setShowDeleteConfirm(null)}>
          <div className="modal-content chat-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Channel</h3>
              <button className="modal-close" onClick={() => !deleting && setShowDeleteConfirm(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="chat-delete-warning">
                <AlertTriangle size={20} className="icon--amber" />
                <p>You are about to delete the channel <strong>"{showDeleteConfirm.name}"</strong>. This will deactivate the channel and all memberships. Messages will be preserved in the audit log.</p>
              </div>
              {showDeleteConfirm.patient_name && (
                <div className="chat-delete-phi-notice">
                  <AlertTriangle size={16} className="icon--red" />
                  <span>This is a patient care channel linked to <strong>{showDeleteConfirm.patient_name}</strong>. Deletion will be logged for HIPAA compliance.</span>
                </div>
              )}
              <label className="form-label" style={{ marginTop: '12px' }}>Reason for deletion <span className="text-light">(required for audit log)</span></label>
              <textarea
                className="form-input chat-delete-reason"
                placeholder="Enter reason for deleting this channel..."
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                rows={3}
              />
            </div>
            <div className="modal-footer">
              <button className="btn-sm btn-ghost" onClick={() => setShowDeleteConfirm(null)} disabled={deleting}>Cancel</button>
              <button
                className="btn-sm btn-danger"
                onClick={handleDeleteChannel}
                disabled={!deleteReason.trim() || deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Channel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Deletion Log Modal ── */}
      {showDeletionLog && (
        <div className="modal-overlay" onClick={() => setShowDeletionLog(false)}>
          <div className="modal-content chat-deletion-log-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Channel Deletion Log</h3>
              <button className="modal-close" onClick={() => setShowDeletionLog(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {logLoading ? (
                <p className="text-secondary">Loading log entries...</p>
              ) : deletionLog.length === 0 ? (
                <p className="text-secondary">No channels have been deleted yet.</p>
              ) : (
                <div className="chat-deletion-log-list">
                  {deletionLog.map((entry, idx) => (
                    <div key={idx} className="chat-deletion-log-entry">
                      <div className="chat-log-entry-header">
                        <span className="chat-log-channel-name">{entry.channelName}</span>
                        <span className="chat-log-type badge badge--info">{entry.channelType}</span>
                        <span className="chat-log-timestamp">{new Date(entry.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="chat-log-entry-details">
                        <span><strong>Deleted by:</strong> {entry.deletedBy?.name} ({entry.deletedBy?.role})</span>
                        <span><strong>Messages archived:</strong> {entry.messageCount}</span>
                        <span><strong>Members:</strong> {entry.members?.map(m => m.name).join(', ')}</span>
                      </div>
                      <div className="chat-log-entry-reason">
                        <strong>Reason:</strong> {entry.reason}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-sm btn-ghost" onClick={() => setShowDeletionLog(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatPage;
